import {
  useCreateAlertMutation,
  useGetChannelDetailsQuery,
  useGetChannelMessagesQuery,
  useGetChannelsQuery,
  useReadChannelMutation,
  useSubscribeChannelMutation,
  useUnsubscribeChannelMutation
} from '@bo-services/alertsApi';
import { DateTime } from 'luxon';
import type { FormattedChannel } from '@library/types/alerts';
import { useCallback } from 'react';

export interface UseChannelsProps {
  channelId?: number;
  isForChannelUsers?: boolean;
  page?: number;
  query?: string;
}

export const useChannels = ({
  channelId,
  isForChannelUsers = false,
  page = 1,
  query = ''
}: UseChannelsProps) => {
  // TODO: Split this into two separate custom hooks for alerts and alertchannels
  const { data: allChannelsData, isFetching: channelsFetching } =
    useGetChannelsQuery();

  const { data: oneChannelData, isFetching: channelFetching } =
    useGetChannelMessagesQuery(
      {
        channelId: channelId || 1,
        page,
        pageSize: 50,
        query
      },
      { skip: !channelId || channelId === 0 || isForChannelUsers }
    );

  const { data: channelDetails, isFetching: channelDetailsIsFetching } =
    useGetChannelDetailsQuery(
      {
        channelId: channelId || 1
      },
      { skip: !channelId || channelId === 0 || isForChannelUsers }
    );

  const [readChannelMutation] = useReadChannelMutation();

  const [createAlertMutation] = useCreateAlertMutation();

  const [unsubscribeFromChannelMutation] = useUnsubscribeChannelMutation();
  const [subscribeToChannelMutation] = useSubscribeChannelMutation();

  const readChannel = useCallback(
    (formattedChannelSelected: FormattedChannel) => {
      if (
        formattedChannelSelected &&
        formattedChannelSelected.id &&
        formattedChannelSelected.isChannelUnread
      ) {
        readChannelMutation({ id: formattedChannelSelected.id }).catch(
          (error) => {
            // eslint-disable-next-line no-console
            console.error('Error reading channel:', error);
          }
        );
      }
    },
    [readChannelMutation]
  );

  const handleCreateAlert = useCallback(
    async (
      alertText: string,
      setAlertSent: (alertSent: boolean) => void,
      setAlertText: (text: string) => void
    ) => {
      if (setAlertSent && channelId) {
        setAlertSent(true);

        const newAlertObj = {
          channelId,
          text: alertText,
          timestamp: DateTime.now().toISO()
        };

        try {
          await createAlertMutation(newAlertObj);
          setAlertText('');
        } catch (error: unknown) {
          // eslint-disable-next-line no-console
          console.log(`Error: ${String(error)}`);
        }
      }
    },
    [channelId, createAlertMutation]
  );

  const handleChannelAction = useCallback(
    async (
      action: string,
      selectedChannelId: number,
      setButtonClicked: (buttonClicked: boolean) => void
    ) => {
      try {
        if (action === 'subscribe') {
          await subscribeToChannelMutation({ id: selectedChannelId });
        } else if (action === 'unsubscribe') {
          await unsubscribeFromChannelMutation({ id: selectedChannelId });
        }
        setButtonClicked(false);
        return true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error performing channel action:', error);
        return false;
      }
    },
    [subscribeToChannelMutation, unsubscribeFromChannelMutation]
  );

  return {
    allChannelsData,
    channelDetails,
    channelDetailsIsFetching,
    channelsFetching,
    oneChannelData,
    channelFetching,
    handleCreateAlert,
    handleChannelAction,
    readChannel
  };
};
